import { createApp, createSSRApp } from 'vue';
import { createPinia } from 'pinia';
import App from './App.vue';
import router from './router';
import { reportCrash } from './stores/crashState';
import { initGA4 } from './analytics/ga4';

// Self-hosted fonts (fixes a real LCP bottleneck, 2026-08-18): these used to
// load via a <link rel="stylesheet"> to fonts.googleapis.com in index.html,
// which forces two extra round-trips to a third-party domain before the
// browser even knows the real font file URLs (HTML -> googleapis.com CSS ->
// gstatic.com font files) -- a well-documented LCP cost, especially painful
// for a text-heavy hero. @fontsource ships the exact same font files
// same-origin, bundled by Vite like any other asset, so this is now just
// disk-local files with zero extra DNS/connection overhead. Only the actual
// weights/styles this app uses are imported (see the removed <link> in
// index.html for the source of truth this list was built from) --
// latin-only, since the app has no non-Latin content.
//
// Real gap fixed 2026-09-08: this used to import @fontsource's own
// per-weight CSS files directly for all three families -- Inter Tight,
// Instrument Serif, AND JetBrains Mono -- which bake in `font-display:
// swap`. Investigating a recurring CLS 0.804 (PageSpeed's "Layout shift
// culprits" named the footer as absorbing the accumulated reflow of every
// text block above it swapping fonts at once) found that Inter Tight and
// Instrument Serif already have their own font-display:optional overrides
// in style.css (added 2026-08-22, a prior and deeper investigation of this
// same bug -- see that file's own extensive comment history), which wins
// the cascade since style.css imports after this. Those two families'
// swap-declaring imports here were always redundant dead weight, so
// they're removed entirely rather than kept alongside an override that
// already beats them. JetBrains Mono was the one family with no override
// anywhere -- `./fonts.css` covers just that gap now.
import './fonts.css';

import './style.css';

// Google Analytics 4 (leadranks.io). Initialised here — synchronously, before
// mount — so the router's per-navigation page_view events (router/index.js
// afterEach) always have a ready gtag(). No-op if the measurement ID is blank.
// See static/src/analytics/ga4.js for why this isn't Google's inline snippet.
initGA4();

// ========== PDF EXPORT MODE ==========
// Set synchronously, before anything mounts, so every component (report
// sections, scroll-reveal animations, IntersectionObserver-gated content)
// can check it from its very first render. Set by core/pdf_export_browser.py
// navigating to /report/:id?pdf=1 in a headless browser.
window.__PDF_EXPORT_MODE__ = new URLSearchParams(window.location.search).get('pdf') === '1';

// ========== CSRF TOKEN MANAGEMENT ==========
let csrfTokenCache = null;
let csrfTokenPromise = null;
let lastTokenRefresh = 0;
const TOKEN_REFRESH_INTERVAL = 30 * 60 * 1000; // 30 minutes

/**
 * Get CSRF token from cookie
 */
const getCsrfTokenFromCookie = () => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; csrf_token=`);
  if (parts.length === 2) {
    const token = parts.pop().split(';').shift();
    if (token) {
      csrfTokenCache = token;
      return token;
    }
  }
  return null;
};

/**
 * Fetch CSRF token from the proper endpoint
 */
const fetchCsrfTokenFromServer = async () => {
  try {
    console.log('🔑 Fetching CSRF token from server...');
    const response = await fetch('/api/csrf-token', {
      credentials: 'include',
      headers: { 'Accept': 'application/json' }
    });
    
    if (response.ok) {
      const data = await response.json();
      // The server sets the cookie; we read it back
      const token = getCsrfTokenFromCookie();
      if (token) {
        console.log('✅ CSRF token obtained from cookie');
        return token;
      }
      // Fallback: if token in response body
      if (data.csrf_token) {
        console.log('✅ CSRF token from response data');
        return data.csrf_token;
      }
    }
  } catch (err) {
    console.warn('⚠️ Could not fetch CSRF token:', err);
  }
  return null;
};

/**
 * Get CSRF token (with caching and auto-refresh)
 */
const getCsrfToken = async (forceRefresh = false) => {
  const now = Date.now();
  
  if (!forceRefresh && csrfTokenCache && (now - lastTokenRefresh) < TOKEN_REFRESH_INTERVAL) {
    return csrfTokenCache;
  }
  
  if (csrfTokenPromise) {
    return csrfTokenPromise;
  }
  
  csrfTokenPromise = (async () => {
    try {
      let token = getCsrfTokenFromCookie();
      if (!token) {
        token = await fetchCsrfTokenFromServer();
      }
      if (token) {
        csrfTokenCache = token;
        lastTokenRefresh = now;
        const meta = document.querySelector('meta[name="csrf-token"]');
        if (meta) meta.setAttribute('content', token);
        console.log(`✅ CSRF token ready (${token.substring(0, 16)}...)`);
        return token;
      }
      console.warn('⚠️ No CSRF token available');
      return null;
    } catch (err) {
      console.error('❌ Failed to get CSRF token:', err);
      return null;
    } finally {
      csrfTokenPromise = null;
    }
  })();
  
  return csrfTokenPromise;
};

// ========== SAVE ORIGINAL FETCH ==========
const originalFetch = window.fetch;

/**
 * Enhanced fetch with automatic CSRF token injection and retry
 */
const fetchWithCSRF = async (url, options = {}) => {
  const method = (options.method || 'GET').toUpperCase();
  const needsCsrf = ['POST', 'PUT', 'PATCH', 'DELETE'].includes(method);
  
  let csrfToken = null;
  let retryCount = 0;
  const maxRetries = 1;
  
  const makeRequest = async (token) => {
    const headers = {
      ...options.headers,
    };
    
    if (needsCsrf && !headers['Content-Type'] && options.body && !(options.body instanceof FormData)) {
      headers['Content-Type'] = 'application/json';
    }
    
    if (needsCsrf && token) {
      headers['X-CSRF-Token'] = token;
      console.log(`🔒 ${method} ${url} with CSRF token`);
    }
    
    return originalFetch(url, {
      ...options,
      headers,
      credentials: 'include'
    });
  };
  
  try {
    if (needsCsrf) {
      csrfToken = await getCsrfToken();
      if (!csrfToken) {
        csrfToken = await getCsrfToken(true);
        if (!csrfToken) {
          throw new Error('CSRF token not available. Please refresh the page.');
        }
      }
    }
    
    let response = await makeRequest(csrfToken);
    
    if (response.status === 403 && retryCount < maxRetries) {
      const errorText = await response.clone().text();
      let errorData = {};
      try {
        errorData = JSON.parse(errorText);
      } catch (e) { /* ignore */ }
      
      if (errorData.code === 'CSRF_INVALID' || errorData.error === 'CSRF validation failed') {
        console.warn(`⚠️ CSRF validation failed (attempt ${retryCount + 1}), refreshing token...`);
        csrfTokenCache = null;
        csrfTokenPromise = null;
        csrfToken = await getCsrfToken(true);
        if (csrfToken) {
          retryCount++;
          response = await makeRequest(csrfToken);
          console.log(`✅ Request retry successful with new CSRF token`);
        }
      }
    }
    
    return response;
    
  } catch (err) {
    console.error(`❌ Fetch error for ${method} ${url}:`, err);
    throw err;
  }
};

// ========== GLOBAL HELPERS ==========

const getCookie = (name) => {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(';').shift();
  return '';
};

const setCsrfTokenInForm = (formElement) => {
  if (!formElement || !csrfTokenCache) return;
  let csrfInput = formElement.querySelector('input[name="csrf_token"]');
  if (!csrfInput) {
    csrfInput = document.createElement('input');
    csrfInput.type = 'hidden';
    csrfInput.name = 'csrf_token';
    formElement.appendChild(csrfInput);
  }
  csrfInput.value = csrfTokenCache;
};

// ========== OVERRIDE GLOBAL FETCH ==========
window.fetch = fetchWithCSRF;

// ========== AUTO-REFRESH TOKEN ==========
let refreshInterval = null;

const startTokenRefresh = () => {
  if (refreshInterval) clearInterval(refreshInterval);
  refreshInterval = setInterval(async () => {
    if (document.visibilityState === 'visible') {
      console.log('🔄 Auto-refreshing CSRF token...');
      csrfTokenCache = null;
      csrfTokenPromise = null;
      await getCsrfToken(true);
    }
  }, TOKEN_REFRESH_INTERVAL);
};

const stopTokenRefresh = () => {
  if (refreshInterval) {
    clearInterval(refreshInterval);
    refreshInterval = null;
  }
};

// ========== INITIALIZATION ==========

const initCsrf = async () => {
  console.log('🔐 Initializing CSRF protection...');
  const token = await getCsrfToken();
  if (token) {
    console.log('✅ CSRF protection initialized successfully');
    const meta = document.querySelector('meta[name="csrf-token"]');
    if (meta) meta.setAttribute('content', token);
  } else {
    console.warn('⚠️ CSRF protection initialized but no token available');
  }
  startTokenRefresh();
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      csrfTokenCache = null;
      getCsrfToken();
    }
  });
};

window.addEventListener('beforeunload', stopTokenRefresh);

// ========== EXPOSE GLOBALLY ==========
window.fetchWithCSRF = fetchWithCSRF;
window.getCsrfToken = getCsrfToken;
window.getCookie = getCookie;
window.setCsrfTokenInForm = setCsrfTokenInForm;

initCsrf();

console.log('✅ Global fetchWithCSRF wrapper installed with auto-refresh');

// ========== VUE APP INITIALIZATION ==========
//
// Hydrate the pre-rendered markup instead of throwing it away.
//
// Marketing routes are served fully rendered from the pre-render cache (see
// core/prerender.py) — ~84KB of real content inside #app. Vue's plain
// createApp().mount() then does this, in its own runtime-dom source:
//
//     if (container.nodeType === 1) container.textContent = "";
//     mount(container, /* isHydrate */ false, ...)
//
// So the browser painted the full page, Vue BLANKED it, then re-rendered it.
// A whole-page layout shift on every pre-rendered route — measured at
// CLS 0.804 on mobile against a "good" threshold of 0.1, and the single
// reason the mobile score sat at 67 rather than ~85.
//
// createSSRApp().mount() passes isHydrate=true, which skips the wipe and
// adopts the existing DOM in place. Applied ONLY when there is markup to
// adopt: routes that are not pre-rendered (login, dashboard, reports) arrive
// with an empty #app, and hydrating nothing is a guaranteed mismatch — those
// still take the plain client-render path.
//
// A hydration mismatch is recoverable by design: Vue warns and patches the
// offending subtree to match the client render. Worst case is a smaller,
// localised shift — never the full-page blank this replaces.
const appContainer = document.querySelector('#app');
const hasPrerenderedMarkup = !!appContainer && appContainer.childElementCount > 0;
const app = hasPrerenderedMarkup ? createSSRApp(App) : createApp(App);

// SSR snapshot hook — ONLY ever exercised inside core/prerender.py's headless
// Chromium, which sets window.__PRERENDER_SSR__ via an add_init_script before
// this module runs. A real visitor never has that flag, so this adds nothing
// to the shipped runtime beyond one boolean check; the SSR renderer itself is
// a lazy `import()` and isn't in the main bundle's eager graph. See
// static/src/prerender-ssr.js for the full rationale (the captured
// client-render DOM lacks the fragment anchors hydration needs, so every
// Fragment vnode warned "expected on client: Symbol(v-fgt)").
if (window.__PRERENDER_SSR__) {
  // Replace #app's client-rendered markup with genuine SSR markup for `path`.
  // The caller unmounts the client app first (window.__lrAppUnmount) so its
  // watchers can't clobber the SSR DOM before the snapshot is captured.
  window.__lrPrerenderSSR = (path) =>
    import('./prerender-ssr.js').then((m) => m.render(path));
}
const pinia = createPinia();
// Global crash boundary — swaps the page for CrashFallback only when
// the error likely corrupts the whole app (e.g., Pinia init failure,
// root-level state corruption). Component-local errors (e.g., a report
// module throwing during render) are logged but DON'T kill the nav.
app.config.errorHandler = (err, instance, info) => {
  console.error('=== VUE ERROR ===');
  console.error('Error:', err?.message);
  console.error('Stack:', err?.stack);
  console.error('Info:', info);
  if (instance) {
    console.error('Component:', instance?.type?.__name || instance?.type?.name || 'anonymous');
  }
  // Only trigger full crash for root-level or Pinia-init failures.
  // Component render errors in pages/reports should NOT take down the
  // entire app — the router.onError handler catches those separately.
  const fatal = !instance || typeof instance?.type === 'object';
  if (fatal) {
    console.error('💥 Fatal error — activating crash fallback');
    reportCrash(err);
  }
};

app.use(pinia);
app.use(router);

// Real bug fixed 2026-09-08: this used to call app.mount() immediately
// after app.use(router), with no wait for the router's initial navigation
// to resolve. Confirmed live via a dev-mode build's console (Vue's real,
// per-element hydration warnings, not the stripped-down production
// one-liner): "Hydration node mismatch ... at <App>" plus "Server rendered
// element contains more child nodes than client vdom" -- both pointing at
// App.vue's own header/nav region. App.vue's NavBar/AppFooter visibility
// (`hideNav = route.meta?.hideNav === true`) and AIChatWidget visibility
// (`showChat`, also route-dependent) both read `route` directly. Vue
// Router resolves the initial route ASYNCHRONOUSLY (every route component
// here is lazy-loaded via dynamic import, and the beforeEach guard awaits
// userStore.fetchUser()) -- mounting before that resolves meant the very
// first hydration pass could run against a not-yet-fully-resolved route,
// while the server's prerendered snapshot was captured from the fully
// resolved state. This is Vue Router's own documented guidance for SSR/
// prerendered hydration: wait for router.isReady() before mounting.
// Bonus: since router.isReady() only resolves once the beforeEach guard's
// fetchUser() call has also settled, this closes the same class of gap
// for userStore.loading-dependent UI (NavBar's auth-state skeleton vs.
// Sign-in/Start-free buttons, confirmed via the same console output).
router.isReady().then(() => {
  app.mount(appContainer || '#app');
  if (window.__PRERENDER_SSR__) {
    window.__lrAppUnmount = () => app.unmount();
  }
});